import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DragDropContext, Draggable } from "@hello-pangea/dnd";
import { StrictModeDroppable as Droppable } from "../../helpers/StrictModeDroppable";
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import axios from '../../api/axios';
import _ from "lodash";
import styles from './Admin.module.css';
import LoadingModal from "../LoadingModal";
import { Link } from "react-router-dom";

const defaultAnswerChoices = [
	{
		displayOrder: 1,
		text: ""
	},
	{
		displayOrder: 2,
		text: ""
	}
];

const validTextRegex = /(.|\s)*\S(.|\s)*/

const EditMultipleChoiceQuestion = () => {

	const navigate = useNavigate();

	let { questionId } = useParams();

	const [text, setText] = useState("");
	const [type, setType] = useState("radio");
	const [shuffleChoices, setShuffleChoices] = useState(false);
	const [status, setStatus] = useState("active");
	const [answerChoices, setAnswerChoices] = useState([...defaultAnswerChoices]);
	const [chartType, setChartType] = useState("pie chart");
	const [maintainAnswerChoiceOrderInChart, setMaintainAnswerChoiceOrderInChart] = useState(false);
	const [feature, setFeature] = useState(true);
	const [priority, setPriority] = useState(1);


	const [textCopy, setTextCopy] = useState("");
	const [typeCopy, setTypeCopy] = useState("radio");
	const [shuffleChoicesCopy, setShuffleChoicesCopy] = useState(false);
	const [statusCopy, setStatusCopy] = useState("active");
	const [answerChoicesCopy, setAnswerChoicesCopy] = useState([...defaultAnswerChoices]);
	const [chartTypeCopy, setChartTypeCopy] = useState("pie chart");
	const [maintainAnswerChoiceOrderInChartCopy, setMaintainAnswerChoiceOrderInChartCopy] = useState(false);
	const [featureCopy, setFeatureCopy] = useState(true);
	const [priorityCopy, setPriorityCopy] = useState(1);

	const [activeAnswerChoices, setActiveAnswerChoices] = useState(0);

	const [validAnswerChoices, setValidAnswerChoices] = useState(0);
	const [validQuestion, setValidQuestion] = useState(false);

	const [isSaving, setIsSaving] = useState(false);
	const [dataLoaded, setDataLoaded] = useState(questionId ? false : true);

	const MAX_ANSWER_CHOICES = 15;


	// const axiosPrivate = useAxiosPrivate();
	// const navigate = useNavigate();
	// const location = useLocation();

	const [open, setOpen] = useState(false);

	const handleClose = (event, reason) => {
		if (reason === 'clickaway') {
			return;
		}
		setOpen(false);
	};

	const parseResponse = (response) => {

		if (response && response.data) {

			console.log(JSON.stringify(response.data));

			setText(response.data.text);
			setType(response.data.type);
			setShuffleChoices(response.data.shuffleChoices);
			setStatus(response.data.status);
			setChartType(response.data.chartType);
			setMaintainAnswerChoiceOrderInChart(response.data.maintainAnswerChoiceOrderInChart);
			setAnswerChoices(_.cloneDeep(response.data.answerChoices));
			setFeature(response.data.feature);
			setPriority(response.data.priority);

			setTextCopy(response.data.text);
			setTypeCopy(response.data.type);
			setShuffleChoicesCopy(response.data.shuffleChoices);
			setStatusCopy(response.data.status);
			setChartTypeCopy(response.data.chartType);
			setMaintainAnswerChoiceOrderInChartCopy(response.data.maintainAnswerChoiceOrderInChart);
			setAnswerChoicesCopy(_.cloneDeep(response.data.answerChoices));
			setFeatureCopy(response.data.feature);
			setPriorityCopy(response.data.priority);

			setIsSaving(false);
			setDataLoaded(true);
		} else {
			console.log("Error: response is not valid");
		}


	}

	const onAnswerChoiceChange = (index, text) => {
		setAnswerChoices(prev => prev.with(index, { ...prev[index], text: text }));
	}
	const onDeleteAnswerChoice = (index) => {
		// if this hasn't been saved to the databse (matrixAnswerChoiceId is undefined), and no text has been set, delete it altogether
		if ((answerChoices[index].matrixAnswerChoiceId === undefined) && (answerChoices[index].text === "")) {
			setAnswerChoices(prev => prev.toSpliced(index, 1));
		} else {
			const deleted = (answerChoices[index].deleted === true ? false : true);
			setAnswerChoices(prev => prev.with(index, { ...prev[index], deleted: deleted }));
		}
	}
	const onAddAnswerChoice = () => {
		const newAnswerChoices = _.cloneDeep(answerChoices);

		let
			max,
			i, l;

		max = 0;

		for (i = 0, l = newAnswerChoices.length; i < l; i++) {
			max = Math.max(max, newAnswerChoices[i].displayOrder);
		}

		newAnswerChoices.push({
			displayOrder: max + 1,
			text: ""
		})

		setAnswerChoices(newAnswerChoices);
	}
	const handleAnswerChoiceOnDragEnd = (result) => {
		if (!result?.destination) return;

		const newAnswerChoices = _.cloneDeep(answerChoices);

		const [reorderedAnswerChoice] = newAnswerChoices.splice(result.source.index, 1);

		newAnswerChoices.splice(result.destination.index, 0, reorderedAnswerChoice);

		setAnswerChoices(newAnswerChoices);

	}
	const saveChanges = () => {
		if (questionId === undefined) {
			saveNewQuestion();
		} else {
			saveUpdatedQuestion();
		}
	}
	const saveUpdatedQuestion = async () => {
		if (!validQuestion || (validAnswerChoices < 2)) {
			return;
		}

		let
			displayOrder = 0;

		const answerChoicesToDelete = [];
		const answerChoicesToUpdate = [];
		const answerChoicesToInsert = [];

		for (let answerChoice of answerChoices) {
			if ((answerChoice.deleted === true) && (answerChoice.multipleChoiceAnswerId !== undefined)) {
				answerChoicesToDelete.push({ multipleChoiceAnswerId: answerChoice.multipleChoiceAnswerId });
			} else {
				displayOrder++;
				answerChoice.displayOrder = displayOrder;
				if (answerChoice.multipleChoiceAnswerId === undefined) {
					answerChoicesToInsert.push(answerChoice);
				} else {
					for (let copy of answerChoicesCopy) {
						if (answerChoice.multipleChoiceAnswerId === copy.multipleChoiceAnswerId) {
							if ((answerChoice.displayOrder !== copy.displayOrder) || (answerChoice.text !== copy.text)) {
								answerChoicesToUpdate.push(answerChoice);
							}
						}
					}
				}
			}
		};

		const json = {};

		json.text = text;
		json.type = type;
		json.shuffleChoices = shuffleChoices;
		json.status = status;
		json.chartType = chartType;
		json.maintainAnswerChoiceOrderInChart = maintainAnswerChoiceOrderInChart;
		json.feature = feature;
		json.priority = priority;
		json.answerChoicesToDelete = _.cloneDeep(answerChoicesToDelete);
		json.answerChoicesToUpdate = _.cloneDeep(answerChoicesToUpdate);
		json.answerChoicesToInsert = _.cloneDeep(answerChoicesToInsert);

		console.log(json);

		if (
			(text !== textCopy) ||
			(type !== typeCopy) ||
			(shuffleChoices !== shuffleChoicesCopy) ||
			(status !== statusCopy) ||
			(chartType !== chartTypeCopy) ||
			(maintainAnswerChoiceOrderInChart !== maintainAnswerChoiceOrderInChartCopy) ||
			(feature !== featureCopy) ||
			(priority !== priorityCopy) ||
			(answerChoicesToDelete.length > 0) ||
			(answerChoicesToUpdate.length > 0) ||
			(answerChoicesToInsert.length > 0)
		) {
			setIsSaving(true);
			try {
				const response = await axios.patch(`/multipleChoiceQuestions/${questionId}`,
					JSON.stringify(json),
					{
						headers: { 'Content-Type': 'application/json' }
					}
				);
				console.log(JSON.stringify(response?.data));
				parseResponse(response);
				setOpen(true);

			} catch (err) {
				console.log(err);
				// if (!err?.response) {
				//     setErrMsg('No Server Response');
				// } else if (err.response?.status === 409) {
				//     setErrMsg('Email Taken');
				// } else {
				//     setErrMsg('Registration Failed')
				// }
				// errRef.current.focus();
			}

		}

	}
	const saveNewQuestion = async () => {
		if (!validQuestion || (validAnswerChoices < 2)) {
			return;
		}

		let
			displayOrder = 0;

		const answerChoicesToInsert = [];

		for (let answerChoice of answerChoices) {
			displayOrder++;
			answerChoice.displayOrder = displayOrder;
			answerChoicesToInsert.push(answerChoice);
		};

		const json = {};

		json.text = text;
		json.type = type;
		json.shuffleChoices = shuffleChoices;
		json.status = status;
		json.chartType = chartType;
		json.maintainAnswerChoiceOrderInChart = maintainAnswerChoiceOrderInChart;
		json.feature = feature;
		json.priority = priority;
		json.answerChoices = _.cloneDeep(answerChoicesToInsert);

		if (answerChoicesToInsert.length >= 2) {
			setIsSaving(true);
			try {
				const response = await axios.post(`/multipleChoiceQuestions`,
					JSON.stringify(json),
					{
						headers: { 'Content-Type': 'application/json' }
					}
				);
				console.log(JSON.stringify(response?.data));

				questionId = response.data.questionId;

				parseResponse(response);

				navigate(`/multiplechoicequestion/edit/${questionId}`, { replace: true });

			} catch (err) {
				console.log(err);
				// if (!err?.response) {
				//     setErrMsg('No Server Response');
				// } else if (err.response?.status === 409) {
				//     setErrMsg('Email Taken');
				// } else {
				//     setErrMsg('Registration Failed')
				// }
				// errRef.current.focus();
			}

		}

	}

	useEffect(() => {

		if (questionId === undefined) {
			return;
		}

		let isMounted = true;

		const controller = new AbortController();

		const getMultipleChoiceQuestion = async () => {

			setIsSaving(true);

			try {
				const response = await axios.get(`/multipleChoiceQuestions/${questionId}`, {
					signal: controller.signal
				});
				if (isMounted) {
					parseResponse(response);
				}
			} catch (err) {
				if (isMounted) {
					console.error(err);
					// if (err.response?.status === 403) {
					//     navigate('/login', { state: { from: location }, replace: true });
					// }
				}
			}
		}

		getMultipleChoiceQuestion();

		return () => {
			isMounted = false;
			controller.abort();
		}
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		let i, l, active, valid;

		active = 0;
		valid = 0;

		for (i = 0, l = answerChoices.length; i < l; i++) {
			if (answerChoices[i].deleted !== true) {
				active++;
				if (validTextRegex.test(answerChoices[i].text)) {
					valid++;
				}
			}
		}
		setActiveAnswerChoices(active);
		setValidAnswerChoices(valid);

	}, [answerChoices]);

	useEffect(() => {
		setValidQuestion(validTextRegex.test(text));
	}, [text]);

	if (!dataLoaded) {
		return <LoadingModal />;
	}

	console.log(`EditMultipleChoiceQuestion: render at ${new Date().getTime()}`);

	return (
		<section className={styles.admin}>
			<p><Link to="/admin">Admin</Link> &gt; <Link to="/questions">Questions</Link></p>

			<h3 className={styles.noBorder}>{(questionId === undefined) ? "New multiple" : "Multiple"}-choice question:</h3>

			<input type="text" name="question" autoComplete="off" autoFocus={true} maxLength="500" placeholder="Who is your favorite Red Sox player?" value={text} onChange={e => setText(e.target.value)} />

			<h3>Type:</h3>

			<div className="radio-button-and-label">
				<input type="radio" id="radio" name="type" value="radio" onChange={(e) => setType(e.target.value)} checked={type === "radio"} />
				<label htmlFor="radio">Radio button</label>
			</div>

			<div className="radio-button-and-label">
				<input type="radio" id="checkbox" name="type" value="checkbox" onChange={(e) => setType(e.target.value)} checked={type === "checkbox"} />
				<label htmlFor="checkbox">Checkbox</label>
			</div>

			<div className="radio-button-and-label">
				<input type="radio" id="select" name="type" value="select" onChange={(e) => setType(e.target.value)} checked={type === "select"} />
				<label htmlFor="select">Drop-down menu</label>
			</div>

			<h3 className={styles.noBorder}>Chart type:</h3>
			{/* <label htmlFor="chartType">Priority (5: highest, 1: lowest)</label> */}
			<select name="chartType" value={chartType} onChange={(e) => setChartType(e.target.value)}>
				<option value="pie chart">Pie chart</option>
				<option value="pie chart with legend">Pie chart with legend</option>
				<option value="bar chart">Bar chart</option>
				<option value="column chart">Column chart</option>
				<option value="waffle chart">Waffle chart</option>
				<option value="nested pie chart">Nested pie chart</option>
				<option value="age crosstab">Age crosstab</option>
				<option value="gender crosstab">Gender crosstab</option>
				<option value="firm type crosstab">Firm type crosstab</option>
				<option value="AUM crosstab">AUM crosstab</option>
				<option value="team crosstab">Team crosstab</option>
				<option value="tenure crosstab">Tenure crosstab</option>
				<option value="region crosstab">Region crosstab</option>
			</select>

			<h3>Status:</h3>

			<div className="radio-button-and-label">
				<input type="radio" id="active" name="status" value="active" onChange={(e) => setStatus(e.target.value)} checked={status === "active"} />
				<label htmlFor="active">Active</label>
			</div>

			<div className="radio-button-and-label">
				<input type="radio" id="archived" name="status" value="archived" onChange={(e) => setStatus(e.target.value)} checked={status === "archived"} />
				<label htmlFor="archived">Archived</label>
			</div>

			<div className="radio-button-and-label">
				<input type="radio" id="deleted" name="status" value="deleted" onChange={(e) => setStatus(e.target.value)} checked={status === "deleted"} />
				<label htmlFor="deleted">Deleted</label>
			</div>

			<h3>In what order should the chart display answer choices?</h3>

			<div className="radio-button-and-label">
				<input type="radio" id="maintainAnswerChoiceOrderInChartFalse" name="maintainAnswerChoiceOrderInChart" onChange={(e) => setMaintainAnswerChoiceOrderInChart(false)} checked={maintainAnswerChoiceOrderInChart === false} />
				<label htmlFor="maintainAnswerChoiceOrderInChartFalse">From largest to smallest value</label>
			</div>

			<div className="radio-button-and-label">
				<input type="radio" id="maintainAnswerChoiceOrderInChartTrue" name="maintainAnswerChoiceOrderInChart" onChange={(e) => setMaintainAnswerChoiceOrderInChart(true)} checked={maintainAnswerChoiceOrderInChart === true} />
				<label htmlFor="maintainAnswerChoiceOrderInChartTrue">In the same order as presented in the question</label>
			</div>

			{/* <label htmlFor="priority">Priority (5: highest, 1: lowest)</label>
            <select name="priority" value={priority} onChange={(e) => setPriority(parseInt(e.target.value, 10))}>
                <option value="5">5 (Highest)</option>
                <option value="4">4 (High)</option>
                <option value="3">3 (Moderate)</option>
                <option value="2">2 (Low)</option>
                <option value="1">1 (Lowest)</option>
            </select> */}

			{/* <div className="checkbox-and-label" style={{ marginTop: '1rem' }}>
                <input type="checkbox" id="feature" name="feature" onChange={(e) => setFeature(e.target.checked)} checked={feature} />
                <label htmlFor="feature">Feature this question on a dashboard</label>
            </div> */}

			<h3>Answer choices:</h3>

			<div className="dndContainer">
				<DragDropContext onDragEnd={handleAnswerChoiceOnDragEnd}>
					<Droppable droppableId="answerChoices">
						{(provided) => (
							<div className="optionsGroup" {...provided.droppableProps} ref={provided.innerRef}>
								{
									answerChoices.map((answerChoice, i) => {
										return (
											<Draggable key={i} draggableId={i.toString()} index={i}>
												{(provided) => (
													<div className="optionGroup" {...provided.draggableProps} {...provided.dragHandleProps} ref={provided.innerRef}>
														<input key={i} type="text" autoComplete="off" maxLength="500" placeholder={`Answer choice ${i + 1}`} disabled={answerChoice.deleted} value={answerChoice.text} onChange={e => onAnswerChoiceChange(i, e.target.value)} />
														<button className="delete" onClick={e => onDeleteAnswerChoice(i)}></button>
													</div>
												)}
											</Draggable>
										)
									})
								}
								{provided.placeholder}
							</div>
						)}
					</Droppable>
				</DragDropContext>
				<button onClick={e => onAddAnswerChoice()} disabled={activeAnswerChoices >= MAX_ANSWER_CHOICES}>Add new answer choice</button>
			</div>
			<div className="checkbox-and-label">
				<input type="checkbox" id="shuffleCheckbox" name="shuffleCheckbox" onChange={(e) => setShuffleChoices(e.target.checked)} checked={shuffleChoices} />
				<label htmlFor="shuffleCheckbox">Randomize (shuffle) the order of statements in the survey</label>
			</div>

			<button disabled={!validQuestion || (validAnswerChoices < 2) || isSaving} onClick={saveChanges}>Save changes</button>
			<Snackbar open={open} autoHideDuration={6000} onClose={handleClose}>
				<Alert onClose={handleClose} variant="filled" severity="success" sx={{ width: '100%' }}>
					Your changes have been saved.
				</Alert>
			</Snackbar>

		</section>
	);

};

export default EditMultipleChoiceQuestion;